import React, { useRef, useState, useEffect } from "react";
import { UploadSimple } from "@phosphor-icons/react";
import atlasApi from "../../utils/atlasapi";
import { dissolveToMultiPolygon } from "../../utils/geometry";
import { parseCadnumList } from "../cadastral/CadastralOverlay";
import { MethodPanel, CreateFooter, InvalidShape } from "./NewProject";

const ACCEPT = [".zip", ".geojson", ".gpkg", ".csv", ".txt"];

const extensionOf = (name) => {
	const i = (name || "").lastIndexOf(".");
	return i === -1 ? "" : name.slice(i).toLowerCase();
};

const formatHa = (ha) => Number(ha || 0).toLocaleString(undefined, { maximumFractionDigits: ha < 100 ? 1 : 0 });

/* Reads a file into a single project outline (GeoJSON Feature).
   Shape/GeoJSON/GPKG go through the convert endpoint. CSV/TXT are lists of
   Bulgarian cadastral numbers, looked up so that numbers not in the cadastre
   can be reported. Resolves to {feature, missing}. */
async function readProjectFile(file) {
	const ext = extensionOf(file.name);
	if (!ACCEPT.includes(ext))
		throw new Error(`${ext || "This file type"} is not supported. Use Shape (.zip), GeoJSON, GPKG or CSV.`);

	if (ext === ".csv" || ext === ".txt") {
		const cadnums = parseCadnumList(await file.text());
		if (!cadnums.length)
			throw new Error("The file contains no cadastral numbers.");
		const resp = await atlasApi.getCadastralFeaturesBG(cadnums);
		const features = resp.features || [];
		if (!features.length)
			throw new Error(`None of the ${cadnums.length} cadastral numbers were found in the cadastre.`);
		return {
			feature: { type: "Feature", properties: {}, geometry: dissolveToMultiPolygon(features) },
			missing: resp.missing || [],
			parcels: features.length
		};
	}

	const formData = new FormData();
	formData.append("file", file);
	const fc = await atlasApi.convertLayer(formData);
	const feature = fc && fc.features && fc.features[0];
	if (!feature || !feature.geometry)
		throw new Error("No shapes were found in the file.");
	if (!["Polygon", "MultiPolygon"].includes(feature.geometry.type))
		throw new Error("The file has no areas. A project outline must be polygons, not points or lines.");
	return { feature, missing: [] };
}

/* Upload tab of the New project panel. Files are picked in the panel or
   dropped anywhere on the map while the tab is open.

   header     : the panel header (title + method tabs)
   mapElement : the map container, which becomes the drop target
   shape      : the outline currently in the draw layer ({source, ha, fileName, missing, ...})
   onShape    : called with (feature, {fileName, missing, parcels}) once a file is read
   onClear    : removes the uploaded outline
   onSave     : opens the save-project dialog
   onFix      : repairs an outline that crosses itself
*/
export default function UploadMethod({ header, mapElement, shape, onShape, onClear, onSave, onFix }) {
	const inputRef = useRef(null);
	const dragDepth = useRef(0);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const [dragging, setDragging] = useState(false);

	const handleFile = async (file) => {
		if (!file || busy) return;
		setBusy(true);
		setError(null);
		try {
			const result = await readProjectFile(file);
			onShape(result.feature, { fileName: file.name, missing: result.missing, parcels: result.parcels });
		} catch (e) {
			setError(e.message || "Could not read the file.");
		} finally {
			setBusy(false);
		}
	};

	// The whole map is a drop target while this tab is open
	useEffect(() => {
		if (!mapElement) return;
		const hasFiles = (e) => e.dataTransfer && [...e.dataTransfer.types].includes("Files");
		const onEnter = (e) => {
			if (!hasFiles(e)) return;
			e.preventDefault();
			dragDepth.current++;
			setDragging(true);
		};
		const onOver = (e) => {
			if (!hasFiles(e)) return;
			e.preventDefault();
			e.dataTransfer.dropEffect = "copy";
		};
		const onLeave = (e) => {
			if (!hasFiles(e)) return;
			dragDepth.current = Math.max(0, dragDepth.current - 1);
			if (dragDepth.current === 0) setDragging(false);
		};
		const onDrop = (e) => {
			if (!hasFiles(e)) return;
			e.preventDefault();
			dragDepth.current = 0;
			setDragging(false);
			handleFile(e.dataTransfer.files[0]);
		};
		mapElement.addEventListener("dragenter", onEnter);
		mapElement.addEventListener("dragover", onOver);
		mapElement.addEventListener("dragleave", onLeave);
		mapElement.addEventListener("drop", onDrop);
		return () => {
			mapElement.removeEventListener("dragenter", onEnter);
			mapElement.removeEventListener("dragover", onOver);
			mapElement.removeEventListener("dragleave", onLeave);
			mapElement.removeEventListener("drop", onDrop);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapElement, busy]);

	const uploaded = shape && shape.source === "upload" ? shape : null;

	return (
		<>
			<MethodPanel
				header={header}
				footer={uploaded && (
					<CreateFooter
						disabled={!!(uploaded.kinks.length || uploaded.overlaps)}
						title={uploaded.fileName}
						meta={`${formatHa(uploaded.ha)} ha total`
							+ (uploaded.parcels ? ` · ${uploaded.parcels} parcel${uploaded.parcels > 1 ? "s" : ""}` : "")}
						onClear={() => { setError(null); onClear(); }}
						onCreate={onSave}
					/>
				)}>
				<button
					className={"np-dropzone " + (busy ? "busy" : "")}
					onClick={() => inputRef.current && inputRef.current.click()}
					disabled={busy}>
					{busy ? <span className="np-spin" aria-hidden="true" /> : <UploadSimple size={32} aria-hidden="true" />}
					<span className="ttl">{busy ? "Reading file…" : <>Drag and drop or <u>browse</u></>}</span>
					<span className="desc">Shape (.zip), GeoJSON, GPKG, or CSV with cadastral numbers</span>
				</button>
				<input
					ref={inputRef}
					type="file"
					accept={ACCEPT.join(",")}
					hidden
					onChange={(e) => { handleFile(e.target.files[0]); e.target.value = ""; }}
				/>

				{error && <div className="np-status error" role="alert">{error}</div>}

				{!uploaded && !error && (
					<div className="np-tip">Tip: you can also drop a file anywhere on the map.</div>
				)}
				{shape && shape.source === "draw" && (
					<div className="np-status warn">Uploading a file replaces the shape you drew.</div>
				)}

				{uploaded && (uploaded.kinks.length > 0 || uploaded.overlaps > 0) && (
					<InvalidShape shape={uploaded} onFix={onFix} />
				)}
				{uploaded && uploaded.missing && uploaded.missing.length > 0 && (
					<div className="np-status warn">
						<b>{uploaded.missing.length} not found in the cadastre:</b> {uploaded.missing.join(", ")}
					</div>
				)}

			</MethodPanel>

			{dragging && (
				<div className="np-drop-overlay" aria-hidden="true">
					<div className="np-drop-card">
						<UploadSimple size={36} />
						<div className="ttl">Drop the file to upload it</div>
						<div className="desc">Shape (.zip), GeoJSON, GPKG or CSV</div>
					</div>
				</div>
			)}
		</>
	);
}
